import React, { memo, useEffect, useState } from "react";
import {
  Stack,
  Grid,
  Typography,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { motion } from "framer-motion";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { useDispatch, useSelector } from "react-redux";
import configServ from "../../../services/config";
import AlertDialog from "../../AlertDialog/AlertDialog";
import { triggerLoader } from "../../../redux/reducers/LoaderTrigger";

// HH:MM (input) <-> HH:MM:SS (api) helpers
const toTimeInput = (t) => (t ? String(t).slice(0, 5) : "");
const toApiTime = (t) => {
  if (!t) return "";
  // already HH:MM:SS
  if (String(t).length >= 8) return String(t).slice(0, 8);
  // HH:MM -> HH:MM:SS
  return `${t}:00`;
};

//FUNCTION
const ShiftsCard = ({ isChanged, setIsChanged, editData, setEditData }) => {
  //#region code
  const dispatch = useDispatch();
  const { company_id, user_id } = useSelector((state) => state.GlobalVariables);

  const formInitial = {
    shift_code: "",
    shift_name: "",
    start_time: "",
    end_time: "",
    is_active: true,
  };
  const [formData, setFormData] = useState(formInitial);
  const [isEdit, setIsEdit] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("Alert");
  const [dialogContent, setDialogContent] = useState("");
  const [buttonType, setButtonType] = useState(null);

  const handleOpenDialog = (heading, content, type) => {
    setTitle(heading);
    setDialogContent(content);
    setButtonType(type);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setTitle("Alert");
    setDialogContent("");
    setButtonType(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: checked,
    }));
  };

  useEffect(() => {
    if (Object.keys(editData).length !== 0) {
      setFormData({
        ...editData,
        start_time: toTimeInput(editData.start_time),
        end_time: toTimeInput(editData.end_time),
        is_active:
          editData.is_active === undefined ? true : Boolean(editData.is_active),
      });
      setIsEdit(true);
    }
  }, [editData]);

  const clear = () => {
    setFormData(formInitial);
    setValidationErrors({});
    setIsEdit(false);
    setEditData({});
  };

  const handleFormValidation = () => {
    const errors = {};
    if (!formData.shift_code) {
      errors.shift_code = "required";
    }
    if (!formData.shift_name) {
      errors.shift_name = "required";
    }
    if (!formData.start_time) {
      errors.start_time = "required";
    }
    if (!formData.end_time) {
      errors.end_time = "required";
    }
    setValidationErrors(errors);
    return Object.values(errors).every((error) => error === "");
  };

  // Map server-side 422 field errors onto the form
  const applyServerErrors = (res) => {
    if (res?.errors && typeof res.errors === "object") {
      const mapped = {};
      Object.keys(res.errors).forEach((key) => {
        mapped[key] = Array.isArray(res.errors[key])
          ? res.errors[key][0]
          : res.errors[key];
      });
      setValidationErrors((prev) => ({ ...prev, ...mapped }));
    }
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      dispatch(triggerLoader());

      const valid = handleFormValidation();
      if (valid === false) {
        return false;
      }

      const dataToSend = {
        ...formData,
        company_id: company_id,
        start_time: toApiTime(formData.start_time),
        end_time: toApiTime(formData.end_time),
      };

      if (!isEdit) {
        dataToSend.created_by = user_id;
        const res = await configServ.createShift(dataToSend);
        if (res.status === 201 || res.status === 200) {
          setIsChanged(!isChanged);
          clear();
          handleOpenDialog(
            "Success",
            res.message || "Shift created successfully.",
            "success"
          );
        } else if (res.status === 422) {
          applyServerErrors(res);
          handleOpenDialog(
            "Warning",
            res.message || "Validation failed.",
            "warning"
          );
        } else {
          handleOpenDialog(
            "Error",
            res.message || "Failed to create shift.",
            "error"
          );
        }
      } else {
        dataToSend.updated_by = user_id;
        const res = await configServ.updateShift(dataToSend);
        if (res.status === 200) {
          setIsChanged(!isChanged);
          clear();
          handleOpenDialog(
            "Success",
            res.message || "Shift updated successfully.",
            "success"
          );
        } else if (res.status === 422) {
          applyServerErrors(res);
          handleOpenDialog(
            "Warning",
            res.message || "Validation failed.",
            "warning"
          );
        } else {
          handleOpenDialog(
            "Error",
            res.message || "Failed to update shift.",
            "error"
          );
        }
      }
    } catch (e) {
      console.log(e);
      handleOpenDialog("Error", "An unexpected error has occurred.", "error");
    } finally {
      dispatch(triggerLoader());
    }
  };
  //#endregion

  return (
    <>
      <AlertDialog
        open={dialogOpen}
        setOpen={handleCloseDialog}
        title={title}
        message={dialogContent}
        buttonTitle="Ok"
        buttonType={buttonType}
      />

      <motion.div
        style={{
          width: "100%",
          backgroundColor: "#e3f3fc",
          padding: 20,
          borderRadius: "10px",
          border: "none",
        }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.1, delay: 0.1 }}
      >
        <Grid container alignItems="flex-end" justifyContent="flex-start" spacing={1}>
          <Grid item xs={12} md={12} justifyContent={"flex-start"}>
            <Typography sx={{ fontSize: "16px", fontWeight: "bolder" }}>
              Shift Detail
            </Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              id="shift_code"
              name="shift_code"
              label="Shift Code*"
              variant="outlined"
              fullWidth
              size="small"
              inputProps={{ maxLength: 20 }}
              value={formData.shift_code || ""}
              onChange={handleChange}
              error={!!validationErrors.shift_code}
              helperText={
                typeof validationErrors.shift_code === "string" &&
                validationErrors.shift_code !== "required"
                  ? validationErrors.shift_code
                  : ""
              }
              sx={{ backgroundColor: "white" }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              id="shift_name"
              name="shift_name"
              label="Shift Name*"
              variant="outlined"
              fullWidth
              size="small"
              inputProps={{ maxLength: 100 }}
              value={formData.shift_name || ""}
              onChange={handleChange}
              error={!!validationErrors.shift_name}
              sx={{ backgroundColor: "white" }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              id="start_time"
              name="start_time"
              label="Start Time*"
              type="time"
              variant="outlined"
              fullWidth
              size="small"
              value={formData.start_time || ""}
              onChange={handleChange}
              error={!!validationErrors.start_time}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 1 }}
              sx={{ backgroundColor: "white" }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              id="end_time"
              name="end_time"
              label="End Time*"
              type="time"
              variant="outlined"
              fullWidth
              size="small"
              value={formData.end_time || ""}
              onChange={handleChange}
              error={!!validationErrors.end_time}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 1 }}
              sx={{ backgroundColor: "white" }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormGroup sx={{ display: "flex", flexDirection: "row" }}>
              <FormControlLabel
                name="is_active"
                control={
                  <Checkbox checked={Boolean(formData.is_active) || false} />
                }
                onChange={handleChangeCheckbox}
                label="Active"
              />
            </FormGroup>
          </Grid>
        </Grid>

        <Grid container spacing={2} justifyContent="space-between" px={2}>
          <Grid
            item
            container
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
            mt={1}
          >
            <Stack direction={"row"} spacing={2}>
              <Button
                onClick={clear}
                variant="contained"
                color="secondary"
                startIcon={<RotateLeftIcon />}
              >
                Reset
              </Button>
              <Button
                onClick={handleSubmit}
                autoFocus
                variant="contained"
                color={isEdit ? "success" : "primary"}
                startIcon={isEdit ? <UpdateIcon /> : <SaveIcon />}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </motion.div>
    </>
  );
};

export default memo(ShiftsCard);
